/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { CheckCircle2, Loader2 } from 'lucide-react';
import { type JSX } from 'react';
import { useTranslation } from 'react-i18next';

import { Modal } from '../../components/ui/modal.js';
import { usePlan } from '../../lib/queries/itp.js';

interface PlanDetailModalProps {
  readonly planId: string | null;
  readonly clientName: string;
  readonly onClose: () => void;
}

function Row({ label, value }: { readonly label: string; readonly value: string }): JSX.Element {
  return (
    <div className="flex items-center justify-between rounded-xl bg-slate-50 px-4 py-3 dark:bg-slate-800">
      <span className="text-sm text-slate-500 dark:text-slate-400">{label}</span>
      <span className="text-sm font-semibold text-slate-800 dark:text-slate-100">{value}</span>
    </div>
  );
}

/**
 * Read-only "Therapy Plan Details" modal (Figma). Lazily fetches the plan detail
 * when opened. Editing goal progress lives on the dedicated detail route reached
 * by "Update Progress".
 */
export function PlanDetailModal({
  planId,
  clientName,
  onClose,
}: PlanDetailModalProps): JSX.Element {
  const { t } = useTranslation();
  const open = planId !== null;
  const plan = usePlan(planId ?? '', open);

  return (
    <Modal
      open={open}
      onOpenChange={onClose}
      title={t('therapist.plans.detailsTitle')}
      closeLabel={t('common.cancel')}
    >
      {plan.isLoading || plan.data === undefined ? (
        <div className="flex justify-center py-10">
          <Loader2 className="h-5 w-5 animate-spin text-slate-400" aria-hidden="true" />
        </div>
      ) : (
        <div className="space-y-3">
          <Row label={t('therapist.plans.client')} value={clientName} />
          <Row label={t('therapist.plans.program')} value={plan.data.name} />
          <Row label={t('therapist.plans.startDate')} value={plan.data.startDate ?? '—'} />
          <Row label={t('therapist.plans.reviewDate')} value={plan.data.reviewDate ?? '—'} />
          <Row label={t('therapist.plans.progress')} value={`${String(plan.data.progressPct)}%`} />
          <div className="rounded-xl bg-slate-50 px-4 py-3 dark:bg-slate-800">
            <p className="mb-2 text-sm text-slate-500 dark:text-slate-400">
              {t('therapist.plans.goals')}
            </p>
            <ul className="space-y-2">
              {plan.data.goals.map((goal) => (
                <li
                  key={goal.id}
                  className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200"
                >
                  <CheckCircle2
                    className="h-4 w-4 shrink-0 text-[var(--accent)]"
                    aria-hidden="true"
                  />
                  {goal.title}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </Modal>
  );
}
